import type { AdminTicket } from "../../types.js";

/**
 * Sort + SLA constants and the pure list helpers for the admin ticket list,
 * extracted from `TicketListPage.tsx` so the sort-toggle / priority-slug / SLA
 * logic unit-tests directly.
 */

/** Allow-listed by Rails' AdminTicketsController#sort_order. */
export type SortKey = "created_at" | "updated_at" | "due_date" | "title" | "key" | "priority_id";
export type SortDir = "asc" | "desc";

export interface SortableColumn {
  key: SortKey;
  /** Visible header label. */
  label: string;
}

/** What the column header arrow rotates around. */
export const SORTABLE: Record<string, SortableColumn> = {
  reference: { key: "key",         label: "Ref" },
  title:     { key: "title",       label: "Title" },
  priority:  { key: "priority_id", label: "Priority" },
  updated:   { key: "updated_at",  label: "Updated" },
};

/** Colour-coded label for the admin index's derived `sla_status` column. */
export const SLA_BADGE: Record<
  AdminTicket["sla_status"],
  { label: string; color: string }
> = {
  none:      { label: "—",         color: "text.disabled" },
  on_track:  { label: "On track",  color: "success.main" },
  at_risk:   { label: "At risk",   color: "warning.main" },
  breached:  { label: "Breached",  color: "error.main" },
};

/** The badge for a row's `sla_status`, falling back to the neutral "none". */
export function slaBadge(status: AdminTicket["sla_status"]): { label: string; color: string } {
  return SLA_BADGE[status] ?? SLA_BADGE.none;
}

/**
 * Click-to-sort transition: clicking the active column flips direction;
 * clicking a new column sorts it ascending. Pure — the page maps the result
 * onto the URL.
 */
export function nextSort(
  currentSort: SortKey,
  currentDir: SortDir,
  clicked: SortKey,
): { sort: SortKey; dir: SortDir } {
  if (currentSort === clicked) {
    return { sort: clicked, dir: currentDir === "asc" ? "desc" : "asc" };
  }
  return { sort: clicked, dir: "asc" };
}

/**
 * The slug the admin index filters priorities by. `SupportPriority` doesn't
 * always carry a `slug`; fall back to the lowercased name (matches the slug
 * Rails generates).
 */
export function prioritySlug(name: string, slug?: string): string {
  return slug ?? name.toLowerCase().replace(/\s+/g, "_");
}
